import { GetTravelSegmentResponse } from '@orbit/api-types'
import React from 'react'
import {
	ColumnContainer,
	ColumnContent,
	ColumnHeader,
	ColumnTitle,
	CustomDayName,
	DateDisplay,
	DayNoteIcon,
	DayNoteText,
	EmptyState,
	EmptyStateText,
	EventContent,
	EventDot,
	EventDotColumn,
	EventDotLine,
	EventDotLineAbove,
	StaticDayNoteRow,
	TimelineEventRow,
} from '../Itinerary/Column/Column.styles'
import TravelSegment from '../Itinerary/TravelSegment'
import { Day } from '../Itinerary/types'
import ReadOnlyCard from './ReadOnlyCard'

interface ReadOnlyColumnProps {
	day: Day
	travelSegments?: GetTravelSegmentResponse[]
}

const ReadOnlyColumn: React.FC<ReadOnlyColumnProps> = ({ day, travelSegments }) => {
	const formatDate = (dateString: string) => {
		const normalized = dateString.includes('-') ? dateString.split('-').join('') : dateString
		const y = Number(normalized.slice(0, 4))
		const m = Number(normalized.slice(4, 6)) - 1
		const d = Number(normalized.slice(6, 8))
		const date = new Date(y, m, d)
		return date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
		})
	}

	const getTravelSegmentBetweenEvents = (currentEventId: number, nextEventId: number) => {
		if (!travelSegments) return null
		return travelSegments.find(
			segment =>
				segment.fromEventId === currentEventId &&
				segment.toEventId === nextEventId &&
				segment.tripDayIndex === Number(day.id),
		)
	}

	const sortedEvents = day.events

	return (
		<ColumnContainer>
			<ColumnHeader>
				<ColumnTitle>
					{day.displayName}
					<DateDisplay>{formatDate(day.date)}</DateDisplay>
					{day.name && <CustomDayName>{day.name}</CustomDayName>}
				</ColumnTitle>
			</ColumnHeader>

			{day.note && (
				<StaticDayNoteRow as="div">
					<DayNoteIcon>📝</DayNoteIcon>
					<DayNoteText>{day.note}</DayNoteText>
				</StaticDayNoteRow>
			)}

			<ColumnContent>
				{sortedEvents.map((event, index) => {
					const nextEvent = sortedEvents[index + 1]
					const travelSegment = nextEvent
						? getTravelSegmentBetweenEvents(event.id, nextEvent.id)
						: null
					const isFirstEvent = index === 0
					const isLastEvent = index === sortedEvents.length - 1

					return (
						<React.Fragment key={event.id}>
							<TimelineEventRow>
								<EventDotColumn>
									<EventDotLineAbove $first={isFirstEvent} />
									<EventDot />
									<EventDotLine $last={isLastEvent} />
								</EventDotColumn>
								<EventContent>
									<ReadOnlyCard event={event} />
								</EventContent>
							</TimelineEventRow>
							{travelSegment && <TravelSegment segment={travelSegment} />}
						</React.Fragment>
					)
				})}

				{sortedEvents.length === 0 && (
					<EmptyState>
						<EmptyStateText>No Events Yet</EmptyStateText>
					</EmptyState>
				)}
			</ColumnContent>
		</ColumnContainer>
	)
}

export default ReadOnlyColumn
